/**
 * Chart Color Tokens — Single Source of Truth
 *
 * Reads brand-aware CSS custom-properties from :root at call-time so that
 * charts, SVG gradients, and Canvas-based libraries (Chart.js, Recharts)
 * always reflect the active brand palette.
 *
 * Returns plain CSS color strings (rgba / rgb) suitable for Chart.js
 * `backgroundColor`, Recharts `fill`, or any inline-style prop.
 *
 * Usage:
 * ```ts
 * import { getSemanticChartTokens } from '@/lib/chartTokens';
 * const t = getSemanticChartTokens();
 * // t.primary        → "rgb(R G B)"          (solid)
 * // t.primaryFill     → "rgba(R G B / 0.5)"  (chart area fill)
 * // t.primaryRgb      → "R G B"              (raw triplet)
 * ```
 *
 * @module lib/chartTokens
 */

// ── helpers ──────────────────────────────────────────────────────────────────

/** Read a CSS custom property from :root. Returns trimmed value or fallback. */
function cssVar(name: string, fallback: string): string {
    if (typeof document === 'undefined') return fallback;
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
}

/** Wrap an "R G B" triplet as `rgb(R G B)`. */
function rgb(triplet: string): string {
    return `rgb(${triplet})`;
}

/** Wrap an "R G B" triplet as `rgba(R G B / alpha)`. */
function rgba(triplet: string, alpha: number): string {
    return `rgb(${triplet} / ${alpha})`;
}

// ── lightweight colour readers (for non-chart code) ─────────────────────────

/**
 * Read a CSS custom property that resolves to a color string (hex/named).
 * Safe for SVG attributes, inline styles, or any context needing a single color.
 *
 * @example brandColor('--color-primary-500', '#3b82f6')
 */
export function brandColor(varName: string, fallback: string): string {
    return cssVar(varName, fallback);
}

/**
 * Build an rgba() color from an RGB-triplet CSS var + alpha.
 * Use the `-rgb` variant of the custom property.
 *
 * @example brandColorAlpha('--color-primary-500-rgb', 0.04, '59 130 246')
 *          → "rgba(59, 130, 246, 0.04)"
 */
export function brandColorAlpha(
    rgbVarName: string,
    alpha: number,
    fallbackRgb: string,
): string {
    const triplet = cssVar(rgbVarName, fallbackRgb);
    const [r, g, b] = triplet.trim().split(/[\s,]+/);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── public types ─────────────────────────────────────────────────────────────

export interface SemanticChartTokens {
    /** Solid primary (blue / brand-primary-500) */
    primary: string;
    /** Primary with 0.5 alpha — chart area / bar fill */
    primaryFill: string;
    /** Primary-400 triplet — lighter shade for dark-mode or gradient start */
    primary400: string;
    primary400Fill: string;
    /** Primary-600 triplet — darker shade for gradient end */
    primary600: string;
    /** Raw "R G B" triplet for primary-500 */
    primaryRgb: string;

    /** Solid accent (cyan / brand-accent-500) */
    accent: string;
    accentFill: string;
    accent400: string;
    accent400Fill: string;
    accent600: string;
    accentRgb: string;

    /** Status / semantic chart colors (solid + fill) */
    success: string;
    successFill: string;
    successBorder: string;
    warning: string;
    warningFill: string;
    warningBorder: string;
    error: string;
    errorFill: string;
    errorBorder: string;
    info: string;
    infoFill: string;
    infoBorder: string;
    collaboration: string;
    collaborationFill: string;
    collaborationBorder: string;

    /** Neutral grid stroke (zinc-200 / zinc-700 depending on mode) */
    neutralGrid: string;
    /** Text / axis color */
    text: string;
    textMuted: string;

    /** Tooltip background (0.95 opacity) — adapts to dark mode */
    tooltipBg: string;
    /** Tooltip border (0.6 opacity) — adapts to dark mode */
    tooltipBorder: string;
    /** Tooltip title/label color */
    tooltipLabelColor: string;
    /** Tooltip item/secondary text color */
    tooltipItemColor: string;
    /** Grid line stroke (0.5 opacity) — adapts to dark mode */
    gridStroke: string;
    /** Axis line border stroke (0.6 opacity) — adapts to dark mode */
    axisLineStroke: string;
}

// ── fallback RGB triplets (AX1 defaults) ────────────────────────────────────

const FB = {
    primary400: '111 150 216',
    primary500: '43 99 197',
    primary600: '36 85 171',
    accent400: '111 150 216',
    accent500: '83 121 174',
    accent600: '44 85 137',
    success500: '15 138 104',
    success400: '15 138 104',
    warning500: '183 121 0',
    warning400: '183 121 0',
    error500: '199 54 85',
    error400: '199 54 85',
    info500: '4 116 196',
    info400: '4 116 196',
    collaboration500: '101 88 200',
    collaboration400: '101 88 200',
    neutralGrid: '226 231 239',
    chartAxis: '102 117 138',
    tooltipBg: '255 255 255',
    tooltipBorder: '229 231 235',
    tooltipText: '38 43 64',
} as const;

// ── public API ───────────────────────────────────────────────────────────────

/**
 * Read brand-aware chart tokens from the live DOM's CSS custom properties.
 *
 * Call this inside a component render or effect — never at module-init, because
 * the document may not exist yet (SSR) or brand tokens may not be injected yet.
 */
export function getSemanticChartTokens(): SemanticChartTokens {
    const p500 = cssVar('--chart-1-rgb', cssVar('--color-primary-500-rgb', FB.primary500));
    const p400 = cssVar('--color-primary-400-rgb', FB.primary400);
    const p600 = cssVar('--color-primary-600-rgb', FB.primary600);
    const a500 = cssVar('--chart-2-rgb', cssVar('--color-accent-500-rgb', FB.accent500));
    const a400 = cssVar('--color-accent-400-rgb', FB.accent400);
    const a600 = cssVar('--color-accent-600-rgb', FB.accent600);

    // Status / semantic chart colors — read from CSS vars with AX1 defaults
    const s500 = cssVar('--chart-3-rgb', cssVar('--color-success-500-rgb', FB.success500));
    const _s400 = cssVar('--color-success-400-rgb', FB.success400);
    const w500 = cssVar('--chart-4-rgb', cssVar('--color-warning-500-rgb', FB.warning500));
    const _w400 = cssVar('--color-warning-400-rgb', FB.warning400);
    const e500 = cssVar('--status-critical-rgb', cssVar('--color-error-500-rgb', FB.error500));
    const _e400 = cssVar('--color-error-400-rgb', FB.error400);
    const i500 = cssVar('--chart-5-rgb', cssVar('--color-info-500-rgb', FB.info500));
    const _i400 = cssVar('--color-info-400-rgb', FB.info400);
    const c500 = cssVar('--chart-6-rgb', cssVar('--color-collaboration-500-rgb', FB.collaboration500));
    const _c400 = cssVar('--color-collaboration-400-rgb', FB.collaboration400);
    const neutralGrid = cssVar('--chart-grid-rgb', FB.neutralGrid);
    const text = cssVar('--chart-tooltip-text-rgb', cssVar('--text-primary-rgb', FB.tooltipText));
    const textMuted = cssVar('--chart-axis-rgb', cssVar('--text-muted-rgb', FB.chartAxis));
    const tBg = cssVar('--chart-tooltip-bg-rgb', FB.tooltipBg);
    const tBorder = cssVar('--chart-tooltip-border-rgb', FB.tooltipBorder);

    return {
        primary: rgb(p500),
        primaryFill: rgba(p500, 0.5),
        primary400: rgb(p400),
        primary400Fill: rgba(p400, 0.6),
        primary600: rgb(p600),
        primaryRgb: p500,

        accent: rgb(a500),
        accentFill: rgba(a500, 0.5),
        accent400: rgb(a400),
        accent400Fill: rgba(a400, 0.6),
        accent600: rgb(a600),
        accentRgb: a500,

        success: rgb(s500),
        successFill: rgba(s500, 0.5),
        successBorder: rgb(s500),
        warning: rgb(w500),
        warningFill: rgba(w500, 0.5),
        warningBorder: rgb(w500),
        error: rgb(e500),
        errorFill: rgba(e500, 0.5),
        errorBorder: rgb(e500),
        info: rgb(i500),
        infoFill: rgba(i500, 0.5),
        infoBorder: rgb(i500),
        collaboration: rgb(c500),
        collaborationFill: rgba(c500, 0.5),
        collaborationBorder: rgb(c500),

        neutralGrid: rgba(neutralGrid, 0.6),
        text: rgb(text),
        textMuted: rgb(textMuted),

        tooltipBg: rgba(tBg, 0.95),
        tooltipBorder: rgba(tBorder, 0.6),
        tooltipLabelColor: rgb(text),
        tooltipItemColor: rgb(textMuted),
        gridStroke: rgba(neutralGrid, 0.5),
        axisLineStroke: rgba(neutralGrid, 0.6),
    };
}

/**
 * Convenience: get an "R G B" triplet for a specific semantic shade.
 * Works for any --color-*-rgb variable.
 *
 * @example getRgbTriplet('primary', 700)  →  "29 78 216"
 */
export function getRgbTriplet(
    role: 'primary' | 'accent' | 'success' | 'warning' | 'error' | 'info' | 'collaboration',
    shade: number,
    fallback = '128 128 128',
): string {
    return cssVar(`--color-${role}-${shade}-rgb`, fallback);
}
